<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { picoSearch } from '@scmmishra/pico-search';
import powerDataAPI from 'dashboard/api/powerData';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PowerDataModal from './components/PowerDataModal.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import { useRelativeTime } from './composables/useRelativeTime';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { relativeTime } = useRelativeTime();
// A BASE é do super admin desde 30/09/2026 (POWERDATA-ACESSO-01): criar, renomear e apagar.
// O administrador e o atendente abrem a base e cuidam das fontes. O servidor recusa com 403;
// aqui é só para não oferecer o que ia falhar.
const { isAdmin, isSuperAdmin } = useAdmin();
const { can } = usePermissions();
// Criar base é do administrador da conta desde 05/10/2026, e de quem recebeu `powerdata_manage`
// num papel personalizado (`can` é falso para o administrador, por isso ele entra à parte). O
// servidor confere o mesmo em `PowerDataController#exigir_papel_da_acao`. Renomear e apagar
// continuam só do super admin: os botões dos cartões seguem em `isSuperAdmin`.
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, o `powerdata:manage` (`power_data#create`), o escopo que o
// servidor pede para criar base; desligada, o papel, como acima.
const { decide } = useAcesso();
const podeCriarBase = computed(
  () =>
    isSuperAdmin.value ||
    decide('powerdata:manage', () => isAdmin.value || can('powerdata_manage'))
);

const stores = ref([]);
const isFetching = ref(true);
const searchQuery = ref('');
const modalRef = ref(null);
const deleteDialogRef = ref(null);
const editingStore = ref(null);
const storeToDelete = ref(null);
const loading = ref({});

const accountId = store.getters.getCurrentAccountId;

const filteredStores = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return stores.value;
  return picoSearch(stores.value, query, ['name']);
});

async function fetchStores() {
  isFetching.value = true;
  try {
    stores.value = await powerDataAPI.index(accountId);
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
}

function openCreate() {
  editingStore.value = null;
  modalRef.value?.open();
}

function openEdit(s) {
  editingStore.value = s;
  modalRef.value?.open();
}

function openDeletePopup(s) {
  storeToDelete.value = s;
  deleteDialogRef.value?.open();
}

async function confirmDeletion() {
  const target = storeToDelete.value;
  if (!target) return;
  loading.value[target.id] = true;
  try {
    await powerDataAPI.destroy(accountId, target.id);
    useAlert(t('POWER_DATA.DELETE_SUCCESS'));
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.DELETE_ERROR'));
  } finally {
    loading.value[target.id] = false;
    storeToDelete.value = null;
    deleteDialogRef.value?.close();
    fetchStores();
  }
}

function goToStore(s) {
  router.push({ name: 'power_data_show', params: { storeId: s.id } });
}

onMounted(fetchStores);
</script>

<template>
  <!-- Sem SettingsLayout de propósito: o `main` dele já é um painel com borda,
       fundo e sombra iguais aos do CardLayout, então card dentro dele vira card
       dentro de card e some. Grade de card pede o fundo da página. -->
  <div class="flex flex-col w-full h-full gap-4 font-inter">
    <BaseSettingsHeader
      v-model:search-query="searchQuery"
      :title="t('POWER_DATA.TITLE')"
      :description="t('POWER_DATA.SUBTITLE')"
      :search-placeholder="t('POWER_DATA.SEARCH_PLACEHOLDER')"
      feature-name="power_data"
    >
      <template v-if="stores.length" #count>
        <span class="text-body-main text-n-slate-11">
          {{ t('POWER_DATA.COUNT', { n: stores.length }) }}
        </span>
      </template>
      <template #actions>
        <Button
          v-if="podeCriarBase"
          :label="t('POWER_DATA.NEW')"
          size="sm"
          @click="openCreate"
        />
        <span v-else class="text-body-small text-n-slate-11">
          {{ t('POWER_DATA.REQUEST_BASE') }}
        </span>
      </template>
    </BaseSettingsHeader>

    <div
      v-if="isFetching"
      class="py-20 text-center text-body-main text-n-slate-11"
    >
      {{ t('POWER_DATA.LOADING') }}
    </div>
    <div
      v-else-if="!filteredStores.length"
      class="py-20 text-center text-body-main text-n-slate-11"
    >
      {{ searchQuery ? t('POWER_DATA.NO_RESULTS') : t('POWER_DATA.NO_STORES') }}
    </div>
    <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <CardLayout
        v-for="s in filteredStores"
        :key="s.id"
        class="cursor-pointer group"
        @click="goToStore(s)"
      >
        <div class="flex items-start w-full gap-3">
          <span
            class="flex items-center justify-center flex-shrink-0 rounded-md size-9 bg-n-alpha-2 text-n-slate-11"
          >
            <i class="i-lucide-database" />
          </span>
          <span
            class="flex-1 min-w-0 font-medium truncate text-body-main text-n-slate-12"
          >
            {{ s.name }}
          </span>
          <span
            v-if="isSuperAdmin"
            class="flex flex-shrink-0 gap-1 transition-opacity opacity-0 group-hover:opacity-100"
            @click.stop
          >
            <Button
              v-tooltip.top="t('POWER_DATA.RENAME')"
              icon="i-lucide-pen-line"
              slate
              xs
              @click="openEdit(s)"
            />
            <Button
              v-tooltip.top="t('POWER_DATA.DELETE')"
              icon="i-lucide-trash-2"
              slate
              xs
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :is-loading="loading[s.id]"
              @click="openDeletePopup(s)"
            />
          </span>
        </div>

        <div class="flex items-center justify-between w-full gap-2">
          <span class="text-body-small text-n-slate-11">
            {{ t('POWER_DATA.LIST.SOURCE_COUNT', { n: s.source_count || 0 }) }}
          </span>
          <span class="text-body-small text-n-slate-10">
            {{ relativeTime(s.updated_at || s.created_at) }}
          </span>
        </div>
      </CardLayout>
    </div>

    <PowerDataModal
      ref="modalRef"
      :store="editingStore"
      @created="fetchStores"
      @updated="fetchStores"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('POWER_DATA.DELETE_TITLE')"
      :description="
        t('POWER_DATA.DELETE_WARNING', { name: storeToDelete?.name || '' })
      "
      :confirm-button-label="t('POWER_DATA.DELETE_YES')"
      :cancel-button-label="t('POWER_DATA.CANCEL')"
      @confirm="confirmDeletion"
    />
  </div>
</template>
